import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  getChecklistRespostaById,
  putChecklistResposta,
} from "../../api/editChecklistApi";
import { ChecklistResposta } from "../../types/editChecklist";
import { RESPOSTA_OPTIONS, descricaoToValor } from "../../utils/respostaMap";

type FormValues = {
  ordem_checklist_resposta: number;
  descricao_checklist_resposta: string | null;
  valor_checklist_respostas: string | null;
};

const EditarResposta: React.FC = () => {
  const { respostaId } = useParams<{ respostaId: string }>();
  const id = Number(respostaId);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {} as FormValues,
  });
  const watchedDescricao = watch("descricao_checklist_resposta");

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token") || "";
        const data: ChecklistResposta = await getChecklistRespostaById(
          token,
          id
        );
        reset({
          ordem_checklist_resposta: data.ordem_checklist_resposta,
          descricao_checklist_resposta: data.descricao_checklist_resposta,
          valor_checklist_respostas: data.valor_checklist_respostas,
        });
      } catch (err) {
        console.error(err);
        toast.error("Erro ao buscar resposta");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, reset]);

  useEffect(() => {
    const auto = descricaoToValor(watchedDescricao ?? null);
    if (watchedDescricao != null) {
      reset((prev) => ({
        ...prev,
        valor_checklist_respostas:
          watchedDescricao === "Desc" ? "null" : auto ?? null,
      }));
    }
  }, [watchedDescricao]);

  const onSubmit = async (form: FormValues) => {
    try {
      const token = localStorage.getItem("token") || "";
      const payload = {
        ...form,
        valor_checklist_respostas:
          form.descricao_checklist_resposta === "Desc"
            ? "null"
            : form.valor_checklist_respostas ?? null,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await putChecklistResposta(token, id, payload as any);
      toast.success("Resposta atualizada");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar resposta");
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Typography variant="h5">Editar Resposta #{id}</Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="ordem_checklist_resposta"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              {...field}
              type="number"
              label="Ordem"
              fullWidth
              margin="normal"
            />
          )}
        />

        <Controller
          name="descricao_checklist_resposta"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Descrição da Resposta"
              fullWidth
              margin="normal"
            >
              {RESPOSTA_OPTIONS.map((opt) => (
                <MenuItem key={opt.label} value={opt.label}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {watch("descricao_checklist_resposta") === "__custom" ? (
          <Controller
            name="descricao_checklist_resposta"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Descrição (custom)"
                fullWidth
                margin="normal"
              />
            )}
          />
        ) : null}

        <Controller
          name="valor_checklist_respostas"
          control={control}
          disabled
          render={({ field }) => (
            <TextField
              {...field}
              label="Valor da Resposta"
              fullWidth
              margin="normal"
              helperText="Será preenchido automaticamente para opções padrão; edite somente se precisar."
            />
          )}
        />

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" type="submit">
            Salvar Resposta
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Voltar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditarResposta;
